/* global webOS */
import { useCallback, useState } from 'react'
import { useLGDetection } from './useLGDetection'

/**
 * Hook for managing LG Smart TV navigation and platform-specific actions
 * @returns {Object} Navigation utilities and handlers
 */
export const useLGNavigation = () => {
  const { isLGSmartTV } = useLGDetection()
  const [navigationError, setNavigationError] = useState(null)

  /**
   * Handle platform-specific back navigation for LG TV
   * Uses webOS.platformBack() for LG TVs, fallback for other platforms
   * @returns {Promise<boolean>} Success status
   */
  const handlePlatformBack = useCallback(async () => {
    setNavigationError(null)

    try {
      if (isLGSmartTV && typeof webOS !== 'undefined') {
        webOS.platformBack() // Platform-specific method for LG Smart TV
        return true
      } else {
        // Fallback for non-LG platforms
        window.close()
        return true
      }
    } catch (error) {
      console.error('Error with platform back navigation:', error)
      const errorMessage =
        "Error occurred. Please try again later or use your remote's back button."
      setNavigationError(errorMessage)
      return false
    }
  }, [isLGSmartTV])

  /**
   * Check if webOS platform API is available
   * @returns {boolean} true if webOS API is available
   */
  const isWebOSAvailable = useCallback(() => {
    return isLGSmartTV && typeof webOS !== 'undefined'
  }, [isLGSmartTV])

  /**
   * Get navigation capabilities for current platform
   * @returns {Object} Navigation capability flags
   */
  const getNavigationCapabilities = useCallback(() => {
    return {
      canUsePlatformBack: isWebOSAvailable(),
      canUseWindowClose: !isLGSmartTV,
      hasRemoteControl: isLGSmartTV,
      supportsWebOSAPI: isWebOSAvailable()
    }
  }, [isLGSmartTV, isWebOSAvailable])

  /**
   * Clear any navigation errors
   */
  const clearNavigationError = useCallback(() => {
    setNavigationError(null)
  }, [])

  /**
   * Handle generic platform exit with error handling
   * @param {Function} onError - Optional error callback
   * @returns {Promise<boolean>} Success status
   */
  const handlePlatformExit = useCallback(
    async onError => {
      const success = await handlePlatformBack()

      if (!success && onError) {
        onError(navigationError)
      }

      return success
    },
    [handlePlatformBack, navigationError]
  )

  return {
    // Core navigation methods
    handlePlatformBack,
    handlePlatformExit,

    // Status and capabilities
    isWebOSAvailable,
    getNavigationCapabilities,

    // Error handling
    navigationError,
    clearNavigationError,

    // Convenience flags
    canUsePlatformBack: isWebOSAvailable(),
    isLGTV: isLGSmartTV
  }
}
